import type { FC, ReactNode } from 'react';
import { createContext } from 'react';

import useContextOrThrowError from '@zen/utils/hooks/useContextOrThrowError';

import { useColumnConfiguration } from '../hooks/useColumnConfiguration';
import type { StateUpdater, VisibilityState } from '../utils/columnStateConverters';

interface TableConfiguration {
  // TanStack-compatible API
  columnVisibility: VisibilityState;
  // Legacy API (kept for backward compatibility)
  hiddenColumns: string[];
  onColumnVisibilityChange: (updater: StateUpdater<VisibilityState>) => void;
  setHiddenColumns: (hiddenColumns: string[]) => void;
  tableId: string;
}

const TableConfigurationContext = createContext<TableConfiguration>({
  hiddenColumns: [],
  setHiddenColumns: () => {},
  columnVisibility: {},
  onColumnVisibilityChange: () => {},
  tableId: ''
});

interface Props {
  children: ReactNode;
  initialHiddenColumns?: string[];
  tableId: string;
}
export const TableConfigurationProvider: FC<Props> = ({ children, tableId, initialHiddenColumns = [] }) => {
  const { hiddenColumns, setHiddenColumns, columnVisibility, onColumnVisibilityChange } = useColumnConfiguration(
    tableId,
    initialHiddenColumns
  );

  return (
    <TableConfigurationContext.Provider
      value={{
        hiddenColumns,
        setHiddenColumns,
        columnVisibility,
        onColumnVisibilityChange,
        tableId
      }}
    >
      {children}
    </TableConfigurationContext.Provider>
  );
};

export default TableConfigurationContext;

export const useTableConfiguration = (): TableConfiguration => {
  return useContextOrThrowError<TableConfiguration>(
    TableConfigurationContext,
    'useTableConfiguration must be used inside TableConfigurationProvider'
  );
};
